import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

export const WORKFLOW_PRESET_RATIOS = [
  { value: '1:1', w: 1, h: 1 },
  { value: '1:2', w: 1, h: 2 },
  { value: '2:3', w: 2, h: 3 },
  { value: '3:2', w: 3, h: 2 },
  { value: '3:4', w: 3, h: 4 },
  { value: '4:3', w: 4, h: 3 },
  { value: '9:16', w: 9, h: 16 },
  { value: '16:9', w: 16, h: 9 },
];

export function RatioWireIcon({ w, h }) {
  const max = 14;
  const scale = max / Math.max(w, h);
  const rw = Math.max(4, Math.round(w * scale));
  const rh = Math.max(4, Math.round(h * scale));
  const x = (18 - rw) / 2;
  const y = (18 - rh) / 2;
  return (
    <svg className="wf-editor-fission__ratio-icon" viewBox="0 0 18 18" aria-hidden="true">
      <rect x={x} y={y} width={rw} height={rh} rx="1.5" />
    </svg>
  );
}

export function resolveAspectRatioLabel(p) {
  const ratio = String(p.aspectRatio || 'original');
  if (ratio === 'original') return i18n.t('workbench:workflowAspectRatio.original');
  if (ratio === 'custom') {
    const w = Number(p.customAspectWidth);
    const h = Number(p.customAspectHeight);
    if (w > 0 && h > 0) return `${w}:${h}`;
    return i18n.t('workbench:workflowAspectRatio.custom');
  }
  return ratio;
}

export default function WorkflowAspectRatioField({ p, onChange, readOnly = false }) {
  const { t } = useTranslation('workbench');
  const aspectRatio = String(p.aspectRatio || 'original');
  const [menuOpen, setMenuOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState({
    width: p.customAspectWidth != null ? String(p.customAspectWidth) : '',
    height: p.customAspectHeight != null ? String(p.customAspectHeight) : '',
  });
  const [customEditing, setCustomEditing] = useState(aspectRatio === 'custom');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  useEffect(() => {
    if (aspectRatio === 'custom') {
      setCustomEditing(true);
      setCustomDraft({
        width: p.customAspectWidth != null ? String(p.customAspectWidth) : '',
        height: p.customAspectHeight != null ? String(p.customAspectHeight) : '',
      });
    }
  }, [aspectRatio, p.customAspectWidth, p.customAspectHeight]);

  const ratioLabel = useMemo(() => resolveAspectRatioLabel(p), [p, i18n.language]);

  const selectRatio = useCallback((next) => {
    if (next === 'custom') {
      setCustomEditing(true);
      setMenuOpen(false);
      onChange({ aspectRatio: 'custom' });
      return;
    }
    setCustomEditing(false);
    setMenuOpen(false);
    onChange({
      aspectRatio: next,
      ...(next !== 'custom' ? { customAspectWidth: undefined, customAspectHeight: undefined } : {}),
    });
  }, [onChange]);

  const confirmCustomRatio = useCallback(() => {
    const w = Math.max(1, Math.floor(Number(customDraft.width) || 0));
    const h = Math.max(1, Math.floor(Number(customDraft.height) || 0));
    if (!w || !h) return;
    onChange({
      aspectRatio: 'custom',
      customAspectWidth: w,
      customAspectHeight: h,
    });
    setCustomEditing(false);
  }, [customDraft.height, customDraft.width, onChange]);

  const cancelCustomRatio = useCallback(() => {
    setCustomEditing(false);
    if (aspectRatio === 'custom' && (!p.customAspectWidth || !p.customAspectHeight)) {
      onChange({ aspectRatio: 'original' });
    }
  }, [aspectRatio, onChange, p.customAspectHeight, p.customAspectWidth]);

  return (
    <div className="wf-editor-fission__ratio-wrap" ref={rootRef}>
      <div className="wf-editor-fission__ratio-picker">
        <button
          type="button"
          className="wf-editor-fission__ratio-trigger"
          disabled={readOnly}
          aria-expanded={menuOpen}
          aria-haspopup="listbox"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <RatioWireIcon w={4} h={3} />
          <span className="wf-editor-fission__ratio-trigger-label">{ratioLabel}</span>
          <span className="wf-editor-fission__ratio-chevron" aria-hidden>▾</span>
        </button>
        {menuOpen ? (
          <div className="wf-editor-fission__ratio-menu" role="listbox" aria-label={t('workflowAspectRatio.menuAria')}>
            <button
              type="button"
              className={`wf-editor-fission__ratio-option${aspectRatio === 'original' ? ' wf-editor-fission__ratio-option--active' : ''}`}
              onClick={() => selectRatio('original')}
            >
              <RatioWireIcon w={4} h={3} />
              <span>{t('workflowAspectRatio.original')}</span>
              {aspectRatio === 'original' ? <span className="wf-editor-fission__ratio-check" aria-hidden>✓</span> : null}
            </button>
            <button
              type="button"
              className={`wf-editor-fission__ratio-option${aspectRatio === 'custom' ? ' wf-editor-fission__ratio-option--active' : ''}`}
              onClick={() => selectRatio('custom')}
            >
              <svg className="wf-editor-fission__ratio-icon" viewBox="0 0 18 18" aria-hidden="true">
                <rect x="3" y="3" width="12" height="12" rx="1.5" strokeDasharray="2 2" />
              </svg>
              <span>{t('workflowAspectRatio.custom')}</span>
              {aspectRatio === 'custom' ? <span className="wf-editor-fission__ratio-check" aria-hidden>✓</span> : null}
            </button>
            <div className="wf-editor-fission__ratio-grid">
              {WORKFLOW_PRESET_RATIOS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`wf-editor-fission__ratio-grid-item${aspectRatio === item.value ? ' wf-editor-fission__ratio-grid-item--active' : ''}`}
                  onClick={() => selectRatio(item.value)}
                >
                  <RatioWireIcon w={item.w} h={item.h} />
                  <span>{item.value}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {customEditing ? (
        <div className="wf-editor-fission__ratio-custom">
          <input
            type="number"
            min="1"
            className="wf-editor-fission__ratio-input"
            placeholder={t('workflowAspectRatio.widthShort')}
            value={customDraft.width}
            disabled={readOnly}
            onChange={(e) => setCustomDraft((prev) => ({ ...prev, width: e.target.value }))}
            aria-label={t('workflowAspectRatio.customWidthAria')}
          />
          <span className="wf-editor-fission__ratio-colon">:</span>
          <input
            type="number"
            min="1"
            className="wf-editor-fission__ratio-input"
            placeholder={t('workflowAspectRatio.heightShort')}
            value={customDraft.height}
            disabled={readOnly}
            onChange={(e) => setCustomDraft((prev) => ({ ...prev, height: e.target.value }))}
            aria-label={t('workflowAspectRatio.customHeightAria')}
          />
          <button
            type="button"
            className="wf-editor-fission__ratio-action"
            disabled={readOnly}
            aria-label={t('workflowAspectRatio.cancelCustomAria')}
            onClick={cancelCustomRatio}
          >
            ×
          </button>
          <button
            type="button"
            className="wf-editor-fission__ratio-action wf-editor-fission__ratio-action--confirm"
            disabled={readOnly}
            aria-label={t('workflowAspectRatio.confirmCustomAria')}
            onClick={confirmCustomRatio}
          >
            ✓
          </button>
        </div>
      ) : null}
    </div>
  );
}
